import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '../../components/ui';
import { analytics, EventTypes } from '../../utils/analytics';
import { User, useUser } from '../../hooks/api/useUser';
import { ROUTE_PATH } from '../../routes/route-paths';
import { useHubspotEventMutation } from '../../hooks/api/useEvent';
import useAuth from '../../hooks/utils/useAuth';
import {
  getMemberPrivacyKey,
  getMemberTermsKey,
  useAcceptVersionedRecord,
  useVersionedRecord,
} from '../../hooks/api/useVersionedRecords';
import { resolveTheme } from '../../themes';
import { Checkbox, Modal, useMediaQuery } from '@mui/material';
import { Header } from '../../components/layout/header';
import { useDefaultSettings } from '../../hooks/api/useDefaultSettings';

const AcceptPolicies: React.FC = () => {
  const { user } = useAuth();
  const isDesktop = useMediaQuery('md');
  const { updateUser } = useUser();
  const hubspotMutation = useHubspotEventMutation();
  const history = useHistory();
  const { data: defaultSettingData } = useDefaultSettings();
  const theme = resolveTheme();
  const memberTermsKey = getMemberTermsKey(theme.id);
  const memberPrivacyKey = getMemberPrivacyKey(theme.id);
  const { data: termsOfService } = useVersionedRecord(memberTermsKey);
  const { data: privacyPolicy } = useVersionedRecord(memberPrivacyKey);
  const { acceptVersionedRecord } = useAcceptVersionedRecord();
  const [termsConditionsChecked, setTermsConditionsChecked] = useState(false);
  const [messaging, setMessaging] = useState(false);
  const [modal, setModal] = useState<'t&c' | 'privacy' | undefined>();
  const acceptPolicyDescription =
    user?.caller_role.active_subscription?.package.client.accept_policy_description ||
    defaultSettingData?.accept_policy_description;

  const handleContinue = async () => {
    const onSuccess = () => {
      hubspotMutation.mutate({
        event: 'onboarding_started',
        properties: {
          package_code: user?.caller_role.active_subscription?.package.code!,
        },
      });

      analytics.trackEvent(EventTypes.COMPLETED_ONBOARDING, user?.client_name, user?.gender || undefined, undefined);

      acceptVersionedRecord.mutate({
        key: memberTermsKey,
        versionId: termsOfService?.id,
      });
      acceptVersionedRecord.mutate({
        key: memberPrivacyKey,
        versionId: privacyPolicy?.id,
      });

      history.push(ROUTE_PATH.HOME);
    };

    updateUser.mutate(
      {
        is_partial: false,
        caller_role: {
          accepts_marketing_emails: messaging,
          notifications_listener_online: messaging,
        },
      } as Partial<User>,
      {
        onSuccess,
      },
    );
  };

  const handleCheckboxChange = () => {
    setTermsConditionsChecked(!termsConditionsChecked);
  };

  return (
    <div className="min-h-screen">
      <div className={`bg-themePrimary ${isDesktop ? 'h-24' : 'h-32'}`}>
        <Header />
        <main className="font-mavenPro m-auto max-w-2xl space-y-9 px-2 pt-16">
          <section>
            {acceptPolicyDescription && (
              <div
                className="prose prose-black prose-strong:font-bold max-w-none"
                dangerouslySetInnerHTML={{ __html: acceptPolicyDescription }}
              ></div>
            )}
          </section>
          <section aria-label="terms-and-privacy-and-messaging-checkboxes">
            <div className="space-y-4">
              <label className="flex items-start gap-2">
                <Checkbox
                  checked={termsConditionsChecked}
                  onChange={handleCheckboxChange}
                  data-testid="terms-and-privacy-checkbox"
                />
                <span className="pt-1">
                  I have read and accept these{' '}
                  <button
                    type="button"
                    onClick={() => setModal('t&c')}
                    className="font-inherit text-button-primary inline cursor-pointer border-0 bg-transparent p-0 hover:underline"
                    data-testid="open-terms-and-conditions-modal"
                  >
                    Terms & Conditions
                  </button>{' '}
                  and{' '}
                  <button
                    type="button"
                    onClick={() => setModal('privacy')}
                    className="font-inherit text-button-primary inline cursor-pointer border-0 bg-transparent p-0 hover:underline"
                    data-testid="open-privacy-policy-modal"
                  >
                    Privacy Policy
                  </button>
                  .
                </span>
              </label>
              <label className="flex items-start gap-2">
                <Checkbox
                  checked={messaging}
                  onChange={() => setMessaging(!messaging)}
                  data-testid="messaging-checkbox"
                  aria-label="Messaging checkbox"
                />
                <span className="pt-1">I agree to receive communications from {theme.name} about this service.</span>
              </label>
            </div>
          </section>

          <section aria-label="activate-account">
            <button
              type="button"
              id="verify-eligibility-button"
              className={`text-bgPrimary w-full rounded-md py-3 font-medium ${
                !termsConditionsChecked ? 'bg-primary/25' : 'bg-buttonPrimary'
              }`}
              data-testid="verify-eligibility-button"
              disabled={!termsConditionsChecked}
              onClick={handleContinue}
            >
              Activate Account
            </button>
          </section>
        </main>
        <Modal open={modal === 't&c'} onClose={() => setModal(undefined)} aria-labelledby="terms-modal-title">
          <div className="m-4 flex max-h-[90vh] flex-col rounded-lg bg-white">
            <div className="flex-1 overflow-auto px-4">
              <div
                className="prose prose-black prose-h2:font-bold prose-strong:font-bold max-w-none pb-4"
                dangerouslySetInnerHTML={{
                  __html: termsOfService?.text ?? '',
                }}
              ></div>
            </div>
            <div className="border-t border-gray-200 p-4">
              <Button onClick={() => setModal(undefined)} className="w-full" variant="primary">
                Close
              </Button>
            </div>
          </div>
        </Modal>
        <Modal open={modal === 'privacy'} onClose={() => setModal(undefined)} aria-labelledby="privacy-modal-title">
          <div className="m-4 flex max-h-[90vh] flex-col rounded-lg bg-white">
            <div className="flex-1 overflow-auto px-4">
              <div
                className="prose prose-black prose-h2:font-bold prose-strong:font-bold max-w-none pb-4"
                dangerouslySetInnerHTML={{
                  __html: privacyPolicy?.text ?? '',
                }}
              ></div>
            </div>
            <div className="border-t border-gray-200 p-4">
              <Button onClick={() => setModal(undefined)} className="w-full" variant="primary">
                Close
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default AcceptPolicies;
